/**
 * « Comment ça marche » — explique le parcours de commande en ligne au client :
 * la fenêtre de commande, la précommande du pain, le retrait par jour choisi, et
 * le prépaiement. Le visiteur pose la question (« quand retirer le pain ? ») dès
 * le catalogue ; on y répond ici, pas au moment de payer (01/09/2026).
 *
 * Modal autonome (ouvert/fermé piloté par le parent) : réutilisé depuis le
 * bandeau du catalogue ET depuis la fiche produit.
 */
import LigneDuTempsPain from './LigneDuTempsPain';
import FenetreFrais from './FenetreFrais';

interface HowItWorksModalProps {
  open: boolean;
  onClose: () => void;
  /** Fenêtre de commande courante, si connue (affichée en tête). */
  windowLabel?: string | null;
}

const OLIVE = '#4a5c3a';
const TERRACOTTA = '#b45f2e';

export default function HowItWorksModal({ open, onClose, windowLabel }: HowItWorksModalProps) {
  if (!open) return null;

  const etapes: Array<{ icon: string; titre: string; texte: string }> = [
    {
      icon: '🗓️',
      titre: 'La plupart des produits se commandent tous les jours',
      texte:
        'Épicerie, boissons, petit-déjeuner, hygiène, pains sur commande : à toute heure, tous les jours. Seuls les produits extra-frais — fruits, légumes, crémerie, traiteur — suivent le rythme des arrivages et des récoltes et s’ouvrent à la commande du mercredi au samedi 18 h, une fois le rayon réapprovisionné.',
    },
    {
      icon: '🥖',
      titre: 'Le pain est fait sur commande',
      texte:
        'Le pain n’est pas pris sur le rayon : il est commandé au boulanger pour vous. On choisit un jour de retrait parmi ceux où il est fabriqué — la fiche du pain affiche ces jours.',
    },
    {
      icon: '🏪',
      titre: 'Retrait en magasin, le jour choisi',
      texte:
        'Pas de livraison : on retire en magasin au jour choisi. Vous venez quand vous recevez le message « commande prête ».',
    },
    {
      icon: '💳',
      titre: 'Payé en ligne, sans frais de préparation',
      texte:
        'Vous payez le prix juste, celui du magasin — aucun frais de préparation. On règle à la commande (carte ou FOLIES). La commande n’est confirmée qu’une fois le paiement reçu — rien à payer au retrait.',
    },
    {
      icon: '🧺',
      titre: 'Vos bacs bleus Folies : un vide contre un plein',
      texte:
        'Le bac Folies est un bac BLEU marqué à notre nom ; il s’achète une fois (10 €, en boutique et en ligne). Rapportez vos bacs bleus vides, propres et en bon état : au retrait, on vous les échange un pour un contre des bacs pleins. Ce n’est pas une consigne : rien n’est remboursé. Indiquez-le simplement au panier. Sans bac bleu, votre commande est préparée dans des caisses de fruits et légumes réutilisées, en carton ou en bois : elles sont offertes, gardez-les ou rapportez-les.',
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="shop-sans bg-[#faf7f0] w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-black/10 shrink-0">
          <h2 className="text-lg font-bold" style={{ color: TERRACOTTA }}>
            Comment ça marche&nbsp;?
          </h2>
          <button
            onClick={onClose}
            className="ml-auto text-3xl leading-none px-2 text-[#6b6550]"
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          {windowLabel && (
            <div className="rounded-xl bg-[#4a5c3a]/10 border border-[#4a5c3a]/25 px-4 py-2.5 text-sm font-semibold" style={{ color: OLIVE }}>
              {windowLabel}
            </div>
          )}
          <FenetreFrais compact />
          {etapes.map((e) => (
            <div key={e.titre} className="flex gap-3">
              <span className="text-2xl leading-none shrink-0">{e.icon}</span>
              <div>
                <div className="font-bold text-[#3a3324]">{e.titre}</div>
                <div className="text-sm text-[#4a4530] mt-0.5">{e.texte}</div>
              </div>
            </div>
          ))}
          {/* Le pain, dessiné : la question que les clients posent le plus. */}
          <LigneDuTempsPain compact />
        </div>

        <div className="px-5 py-4 border-t border-black/10 shrink-0">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl font-bold text-white"
            style={{ backgroundColor: TERRACOTTA }}
          >
            J’ai compris
          </button>
        </div>
      </div>
    </div>
  );
}
